/**
 * Erreurs structurées du simulateur + conversions vers la console d'erreurs (A11)
 * et vers la « remise au LLM » (chatbot pédagogique).
 *
 * Trois familles seulement, volontairement :
 * - `syntax`      : le code n'est pas du C# valide, OU une erreur réelle du compilateur C#
 *                   que nous reproduisons à des fins pédagogiques (CS0664, CS1612, CS0266…).
 * - `unsupported` : c'est peut-être du C# valide, mais hors de la liste blanche de cet
 *                   exercice (coroutines, Rigidbody, List<>…). `symbol` = le symbole exact.
 * - `runtime`     : le programme a compilé mais a échoué pendant la simulation
 *                   (division par zéro, variable non initialisée, boucle infinie probable).
 */

export type SimErrorKind = 'syntax' | 'unsupported' | 'runtime';

export interface SimError {
  kind: SimErrorKind;
  /** Message en français, précis (attendu / trouvé). */
  message: string;
  /** Ligne 1-based dans le source de l'étudiant. */
  line: number;
  /** Colonne 1-based (absente pour certaines erreurs d'exécution). */
  column?: number;
  /** Symbole exact rencontré (`StartCoroutine`, `List<int>`, `transform.localScale`…). */
  symbol?: string;
  /** Piste de correction en français, quand on en a une. */
  hint?: string;
  /** Code du vrai compilateur C# quand l'erreur en reproduit une (`CS0664`, `CS1612`…). */
  csharpCode?: string;
}

/** Exception interne : porte un `SimError`, ne sort jamais de l'API publique (`compile` la capture). */
export class SimCompileError extends Error {
  constructor(public readonly error: SimError) {
    super(error.message);
    this.name = 'SimCompileError';
  }
}

export function syntaxError(message: string, line: number, column?: number, extra?: Partial<SimError>): SimError {
  return { kind: 'syntax', message, line, column, ...extra };
}

export function unsupportedError(symbol: string, message: string, line: number, column?: number, hint?: string): SimError {
  return { kind: 'unsupported', symbol, message, line, column, hint };
}

export function runtimeError(message: string, line: number, extra?: Partial<SimError>): SimError {
  return { kind: 'runtime', message, line, ...extra };
}

// --- Console d'erreurs A11 (frontend/src/pages/ExercisePage.tsx, interface locale `CompilationIssue`) ---

/**
 * Même forme que l'interface locale `CompilationIssue` d'ExercisePage.tsx (non exportée là-bas).
 * Le message commence par « Ligne N: » pour rester lisible et compatible avec `parseIssueLine`.
 */
export interface CompilationIssue {
  severity: 'error' | 'warning';
  line: number | null;
  message: string;
}

const KIND_LABEL: Record<SimErrorKind, string> = {
  syntax: 'Erreur de syntaxe',
  unsupported: 'Hors périmètre',
  runtime: "Erreur à l'exécution",
};

export function formatSimError(err: SimError): string {
  const where = err.line > 0 ? `Ligne ${err.line}${err.column ? `, colonne ${err.column}` : ''}: ` : '';
  const symbol = err.kind === 'unsupported' && err.symbol ? `\`${err.symbol}\` — ` : '';
  const code = err.csharpCode ? ` (${err.csharpCode})` : '';
  const hint = err.hint ? ` → ${err.hint}` : '';
  return `${where}${KIND_LABEL[err.kind]} : ${symbol}${err.message}${code}${hint}`;
}

export function toCompilationIssues(errors: readonly SimError[], warnings: readonly string[] = []): CompilationIssue[] {
  const issues: CompilationIssue[] = errors.map((err) => ({
    severity: 'error' as const,
    line: err.line > 0 ? err.line : null,
    message: formatSimError(err),
  }));
  for (const warning of warnings) {
    issues.push({ severity: 'warning', line: parseLeadingLine(warning), message: warning });
  }
  return issues;
}

function parseLeadingLine(message: string): number | null {
  const match = message.match(/^\s*(?:ligne|line)\s+(\d{1,5})\s*[:.)\-]?/i);
  if (!match) return null;
  const line = parseInt(match[1], 10);
  return Number.isFinite(line) && line > 0 ? line : null;
}

// --- Remise au LLM ---

export interface LlmHandoff {
  kind: SimErrorKind;
  symbol?: string;
  line: number;
  /** Message brut du simulateur (FR). */
  message: string;
  /** 3 lignes autour de l'erreur (ligne − 1 … ligne + 1), préfixées par leur numéro. */
  snippet: string;
  /** Question en français, prête à être envoyée au chatbot pédagogique. */
  question: string;
}

export function extractSnippet(source: string, line: number, radius = 1): string {
  const lines = source.split(/\r?\n/);
  if (lines.length === 0 || line < 1) return '';
  const from = Math.max(1, line - radius);
  const to = Math.min(lines.length, line + radius);
  const width = String(to).length;
  const out: string[] = [];
  for (let n = from; n <= to; n++) {
    const marker = n === line ? '>' : ' ';
    out.push(`${marker} ${String(n).padStart(width, ' ')} | ${lines[n - 1]}`);
  }
  return out.join('\n');
}

export function toLlmHandoff(err: SimError, source: string): LlmHandoff {
  const snippet = extractSnippet(source, err.line);
  const fenced = snippet ? `\n\`\`\`csharp\n${snippet}\n\`\`\`` : '';
  let question: string;
  switch (err.kind) {
    case 'unsupported':
      question =
        `Dans le simulateur Time.deltaTime, j'ai utilisé \`${err.symbol ?? '?'}\` à la ligne ${err.line} ` +
        `et le compilateur pédagogique répond : « ${err.message} ».${fenced}\n` +
        `Pourquoi ce n'est pas au programme de cet exercice, et qu'est-ce que je dois utiliser à la place ? ` +
        `Explique-moi sans écrire la solution complète.`;
      break;
    case 'syntax':
      question =
        `Dans le simulateur Time.deltaTime, mon code C# donne cette erreur à la ligne ${err.line} : « ${err.message} »` +
        `${err.csharpCode ? ` (erreur C# ${err.csharpCode})` : ''}.${fenced}\n` +
        `Peux-tu m'expliquer ce que signifie cette erreur et comment la corriger, sans me donner la solution complète ?`;
      break;
    default:
      question =
        `Dans le simulateur Time.deltaTime, mon code compile mais plante pendant la simulation à la ligne ${err.line} : ` +
        `« ${err.message} ».${fenced}\n` +
        `Qu'est-ce qui se passe à l'exécution et comment l'éviter ? Explique-moi sans écrire la solution complète.`;
  }
  return { kind: err.kind, symbol: err.symbol, line: err.line, message: err.message, snippet, question };
}
